import { Capacitor } from '@capacitor/core'
import { defineStore } from 'pinia'

let _madopsResetTimeout: ReturnType<typeof setTimeout> | null = null
let _madopsInhibitFirstRegisterSet = false
let _crispChatListenersRegistered = false

export const useBaseStore = defineStore('platform', () => {
  const platform = ref<'ios' | 'android' | 'web' | string>('web')
  const version = ref<string>('3.2.9')
  const versionInt = ref<number>(329)
  const promos = ref<any>(null)
  const promoStatus = ref<'valid' | 'expired' | 'invalid' | 'not_started' | 'none'>('none')
  /** Promo from a deep-link `?code=` — not used by menus/banners. */
  const linkedPromo = ref<any>(null)
  const linkedPromoStatus = ref<'valid' | 'expired' | 'invalid' | 'not_started' | 'none'>('none')
  const modalOpenCount = ref(0)
  const showAds = ref<boolean>(true)
  const showNativeStickyAd = ref(true)
  const crispChatOpen = ref(false)
  const showAisles = ref(true)
  /** After `/splash/check`: whether native sticky may show (subject to Extra, circulaire, modales, etc.). */
  const nativeStickyAllowedBySplash = ref(false)

  const userStore = useUserStore()

  let _nativeStickySplashTimer: ReturnType<typeof setTimeout> | null = null

  function setNativeStickySplashPolicy(policy: { allow: boolean, delayMs: number }) {
    if (_nativeStickySplashTimer) {
      clearTimeout(_nativeStickySplashTimer)
      _nativeStickySplashTimer = null
    }
    if (!policy.allow) {
      nativeStickyAllowedBySplash.value = false
      return
    }
    if (policy.delayMs <= 0) {
      nativeStickyAllowedBySplash.value = true
      return
    }
    nativeStickyAllowedBySplash.value = false
    _nativeStickySplashTimer = setTimeout(() => {
      _nativeStickySplashTimer = null
      nativeStickyAllowedBySplash.value = true
    }, policy.delayMs)
  }

  function setNativeStickyAdVisible(visible: boolean) {
    showNativeStickyAd.value = visible
  }

  function incrementModalOpen() {
    modalOpenCount.value++
  }

  function decrementModalOpen() {
    modalOpenCount.value = Math.max(0, modalOpenCount.value - 1)
  }

  // Computed property to determine if promos should be shown
  // Show if: (web AND not logged in) OR (logged in AND (doesn't have Extra OR is in trial))
  // Don't show if: mobile platform OR (logged in AND has Extra AND not in trial)
  const shouldShowPromos = computed(() => {
    // On mobile platforms, don't show promos to non-logged users
    if (platform.value !== 'web' && !userStore.user)
      return false

    // On web, show promos to non-logged users
    if (!userStore.user)
      return true // Not logged in (web only)
    if (userStore.user.extra && userStore.user.extra_active_trial)
      return true // Has Extra but in trial
    if (!userStore.user.extra)
      return true // Logged in but no Extra
    return false // Has Extra and not in trial
  })

  // Détecter la plateforme avec Capacitor
  function detectPlatform() {
    if (import.meta.client) {
      try {
        const capacitorPlatform = Capacitor.getPlatform()
        platform.value = capacitorPlatform as 'ios' | 'android' | 'web' | string
      }
      catch {
        // Fallback si Capacitor n'est pas disponible
        console.warn('Capacitor not available, using web platform')
        platform.value = 'web'
      }
    }
  }

  // Initialiser la plateforme au montage
  if (import.meta.client) {
    detectPlatform()
  }

  function registerCrispChatListeners() {
    if (_crispChatListenersRegistered || !import.meta.client || typeof window === 'undefined')
      return
    const crisp = (window as any).$crisp
    if (!crisp)
      return
    _crispChatListenersRegistered = true
    crisp.push(['on', 'chat:opened', () => {
      crispChatOpen.value = true
    }])
    crisp.push(['on', 'chat:closed', () => {
      crispChatOpen.value = false
      crisp.push(['do', 'chat:hide'])
    }])
  }

  function openCrispChat() {
    if (import.meta.client && typeof window !== 'undefined' && (window as any).$crisp) {
      const crisp = (window as any).$crisp
      crispChatOpen.value = true
      registerCrispChatListeners()
      crisp.push(['do', 'chat:show'])
      crisp.push(['do', 'chat:open'])
      plausible('open_live_support')
    }
  }

  // Schedule madops reset (debounced, SPA-compatible: inhibitFirstRegister + reset with ad divs)
  function scheduleMadopsReset() {
    if (!import.meta.client)
      return
    // eslint-disable-next-line no-console
    console.log('[madops] scheduleMadopsReset called')
    if (_madopsResetTimeout != null)
      clearTimeout(_madopsResetTimeout)
    _madopsResetTimeout = setTimeout(() => {
      _madopsResetTimeout = null
      // eslint-disable-next-line no-console
      console.log('[madops] debounce fired, running reset')
      const madops = (typeof window !== 'undefined' && (window as any).madops)
      if (!madops || typeof madops.reset !== 'function') {
        // eslint-disable-next-line no-console
        console.log('[madops] skip: madops not available or no reset')
        return
      }
      const adDivs = document.querySelectorAll('[data-m32-ad="true"]')
      // eslint-disable-next-line no-console
      console.log('[madops] ad divs found:', adDivs.length)
      if (!_madopsInhibitFirstRegisterSet) {
        madops.inhibitFirstRegister = true
        _madopsInhibitFirstRegisterSet = true
        // eslint-disable-next-line no-console
        console.log('[madops] inhibitFirstRegister = true (first load)')
      }
      // eslint-disable-next-line no-console
      console.log('[madops] calling madops.reset with', adDivs.length, 'divs')
      madops.reset()
    }, 20)
  }

  // Function to track recipe prints
  async function trackRecipePrints(recipes: any[], category: string = 'recipe_listing') {
    const config = useRuntimeConfig()
    const userStore = useUserStore()

    try {
      const p_recipes = []
      for (let i = 0; i < recipes.length; i++) {
        const t_recipe = {
          id: recipes[i].id,
          website_id: recipes[i].website_id,
        }
        p_recipes.push(t_recipe)
      }

      const response = await $fetch(`${config.public.apiUrl}/spectrum/save`, {
        method: 'POST',
        body: {
          recipes: p_recipes,
          category,
        },
        headers: {
          Authorization: `Bearer ${userStore.access_token}`,
        },
      })
    }
    catch (err: any) {
    }
  }

  return {
    platform,
    version,
    versionInt,
    detectPlatform,
    openCrispChat,
    registerCrispChatListeners,
    trackRecipePrints,
    scheduleMadopsReset,
    promos,
    promoStatus,
    linkedPromo,
    linkedPromoStatus,
    shouldShowPromos,
    showAds,
    modalOpenCount,
    incrementModalOpen,
    decrementModalOpen,
    showNativeStickyAd,
    crispChatOpen,
    setNativeStickyAdVisible,
    nativeStickyAllowedBySplash,
    setNativeStickySplashPolicy,
    showAisles,
  }
})
